import { Fragment, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    // Chip,
    Container,
    Collapse,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    // MenuItem,
    // FormControl,
    // InputLabel,
    // Select,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { DriverServiceUrl, endpoints } from 'utils/factory';

const headCells = [
    { id: 'sno', label: '#', sortable: false },
    { id: 'sheet_name', label: 'Sheet' },
    { id: 'uploaded_by', label: 'Uploaded By' },
    { id: 'uploaded_at', label: 'Uploaded At' },
    { id: 'runsheet_date', label: 'Runsheet Date' },
    { id: 'jobs_count', label: 'Jobs Count' },
    // { id: 'delivery_status', label: 'Status' },
];

const rowsPerPageOptions = [5, 10, 25, 50];

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const formatDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString();
    } catch (e) {
        return iso;
    }
};

// const getStatusLabel = (s) => (s === 0 || s === '0' ? 'Pending' : s === 1 || s === '1' ? 'Delivered' : 'Unknown');
const getStatusSortValue = (s) => (s === 1 || s === '1' ? 1 : s === 0 || s === '0' ? 0 : -1);

const descendingComparator = (a, b, orderBy) => {
    const aValue = a?.[orderBy];
    const bValue = b?.[orderBy];

    if (orderBy === 'delivery_status') {
        return getStatusSortValue(bValue) - getStatusSortValue(aValue);
    }

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
};

const getComparator = (order, orderBy) => (order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy));

const stableSort = (array, comparator) => {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
};

const getGroupKey = (row) => {
    const uploadedAt = row?.uploaded_at
        ? new Date(row.uploaded_at).toISOString().replace(/\.\d{3}Z$/, 'Z')
        : '';

    return JSON.stringify([
        row?.sheet_name ?? '',
        row?.uploaded_by ?? '',
        uploadedAt,
        row?.runsheet_date ?? '',
    ]);
};

export default function RunnerList() {
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter] = useState('all');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('sno');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();
    

    const fetchRunnerList = async () => {
        try {
            setIsLoading(true);
            setError('');

            const response = await axios.post(`${DriverServiceUrl}/${endpoints.fetchRunners}`, {});
            const data = response?.data;

            if (!data) {
                setError('No data returned from server.');
                setRows([]);
                return;
            }

            if (data?.success === false || data?.status === false) {
                setError(data?.message || 'Failed to fetch runsheet data.');
                setRows([]);
                return;
            }

            const list = Array.isArray(data?.data) ? data.data : [];
            setRows(list);
        } catch (err) {
            setError(err?.response?.data?.message || 'Server error while fetching runsheet data.');
            setRows([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRunnerList();
    }, []);

    useEffect(() => setPage(0), [searchText, statusFilter, order, orderBy]);

    const groupedRows = useMemo(() => {
        const groups = new Map();

        rows.forEach((row, index) => {
            const key = getGroupKey(row);

            if (!groups.has(key)) {
                groups.set(key, {
                    ...row,
                    groupKey: key,
                    sno: index + 1,
                    jobs: [row],
                    jobs_count: 1,
                });
                return;
            }

            const group = groups.get(key);
            group.jobs.push(row);
            group.jobs_count = group.jobs.length;
        });

        return Array.from(groups.values());
    }, [rows]);

    const filtered = useMemo(() => {
        const q = normalizeText(searchText);
        return groupedRows.filter((group) => {
            const matchesStatus = statusFilter === 'all' || group.jobs.some((job) => String(job?.delivery_status ?? '') === statusFilter);
            const groupHay = [group?.sheet_name, group?.uploaded_by].map(normalizeText).join(' ');
            const matchesChild = group.jobs.some((job) => {
                const childHay = [
                    job?.invoice_id,
                    job?.goods_summary,
                    job?.customer_name,
                    job?.driver_name,
                ]
                    .map(normalizeText)
                    .join(' ');

                return childHay.includes(q);
            });

            const matchesSearch = !q || groupHay.includes(q) || matchesChild;
            return matchesStatus && matchesSearch;
        });
    }, [groupedRows, searchText, statusFilter]);

    const sorted = useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);
    const paginated = useMemo(() => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sorted, page, rowsPerPage]);

    const toggleExpanded = (groupKey) => {
        setExpanded((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleRunnerImport = () => {
        navigate("/app/runner-import");
    };

    return (
        <>
            <Helmet>
                <title>Runner List</title>
            </Helmet>

            <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 4 }}>
                <Container maxWidth="xl">
                    <Paper elevation={2} style={{ borderRadius: 16, overflow: 'hidden' }}>
                        <Box sx={{ background: 'linear-gradient(135deg, #000000 0%, #0f0f0f 40%, #1c1c1c 100%)', color: '#fff', px: 3, py: 3 }}>
                            <Box
                            sx={{
									display: 'flex',
									flexDirection: { xs: 'column', md: 'row' },
									alignItems: { xs: 'flex-start', md: 'center' },
									justifyContent: 'space-between',
									gap: 2,
								}}
                            >
                                <Box>
                                    <Typography variant="h4">Runner List</Typography>
                                    <Typography variant="body2" style={{ opacity: 0.85, marginTop: 8 }}>Runsheet jobs uploaded and processed.</Typography>
                                </Box>

                                <Button
                                    color="secondary"
                                    variant="contained"
                                    onClick={handleRunnerImport}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#111827',
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    + Import Runner
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Search" placeholder="Invoice, customer, driver, sheet..." variant="outlined" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                                </Grid>
                                {/* <Grid item xs={12} md={3}>
                                    <FormControl variant="outlined" fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="0">Pending</MenuItem>
                                            <MenuItem value="1">Delivered</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid> */}
                                {/* <Grid item xs={12} md={3} /> */}
                            </Grid>

                            {error ? <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert> : null}

                            <TableContainer style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }} />
                                            {headCells.map((hc) => (
                                                <TableCell key={hc.id} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {hc.sortable === false ? hc.label : (
                                                        <TableSortLabel active={orderBy === hc.id} direction={orderBy === hc.id ? order : 'asc'} onClick={() => handleRequestSort(hc.id)}>
                                                            {hc.label}
                                                        </TableSortLabel>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={headCells.length + 1} align="center" style={{ padding: 32 }}>
                                                    <Typography>Loading...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : paginated.length > 0 ? (
                                            paginated.map((r, idx) => {
                                                const isExpanded = !!expanded[r?.groupKey];

                                                return (
                                                    <Fragment key={r?.groupKey || r?.sno || idx}>
                                                        <TableRow hover>
                                                            <TableCell>
                                                                <IconButton size="small" onClick={() => toggleExpanded(r?.groupKey)} aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                                                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                                                            <TableCell>{r?.sheet_name || '-'}</TableCell>
                                                            <TableCell>{r?.uploaded_by || '-'}</TableCell>
                                                            <TableCell>{formatDate(r?.uploaded_at)}</TableCell>
                                                            <TableCell>{formatDate(r?.runsheet_date)}</TableCell>
                                                            <TableCell>{r?.jobs_count ?? 0}</TableCell>
                                                            {/* <TableCell>
                                                                <Chip size="small" label={getStatusLabel(r?.delivery_status)} style={{ backgroundColor: getStatusSortValue(r?.delivery_status) === 1 ? '#e7f7ed' : '#fbe9e7', color: getStatusSortValue(r?.delivery_status) === 1 ? '#146c2e' : '#c62828', fontWeight: 600 }} />
                                                            </TableCell> */}
                                                        </TableRow>

                                                        <TableRow>
                                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length + 1}>
                                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ margin: 1 }}>
                                                                        <Table size="small" aria-label="runsheet jobs">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Invoice ID</TableCell>
                                                                                    <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Goods</TableCell>
                                                                                    <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Qty</TableCell>
                                                                                    <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Customer</TableCell>
                                                                                    <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Driver</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {r?.jobs?.map((job, jobIdx) => (
                                                                                    <TableRow hover key={job?.invoice_id || job?.sno || jobIdx}>
                                                                                        <TableCell>{job?.invoice_id || '-'}</TableCell>
                                                                                        <TableCell>{job?.goods_summary || '-'}</TableCell>
                                                                                        <TableCell>{job?.qty ?? '-'}</TableCell>
                                                                                        <TableCell>{job?.customer_name || '-'}</TableCell>
                                                                                        <TableCell>{job?.driver_name || '-'}</TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    </Fragment>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={headCells.length + 1} align="center" style={{ padding: 32 }}>
                                                    <Typography>No records found.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination component="div" count={filtered.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={rowsPerPageOptions} />
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </>
    );
}
